import React, { useState, useEffect } from 'react'
import {
  Calendar, Plus, Edit2, Trash2, Save, X,
  School, MapPin, Clock, Bus, RefreshCw,
  CalendarDays, List, Grid3x3, ChevronLeft, ChevronRight,
  Link as LinkIcon, XCircle, Clock3, Laptop
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import { extractDateInTimezoneSync } from '@/utils/timezoneUtils'
import toast from 'react-hot-toast'
import {
  format, parseISO, isAfter, isSameDay, startOfToday, addDays,
  startOfWeek, endOfWeek, startOfMonth, endOfMonth,
  eachDayOfInterval, addWeeks, subWeeks, addMonths, subMonths,
  getDay, isSameMonth, isToday, getDate
} from 'date-fns'
import { UnifiedAddEventModal } from '@/components/calendar/UnifiedAddEventModal'

interface District {
  id: string
  district_name: string
  district_code?: string
  school_year: string
  start_date: string
  end_date: string
  color?: string
  is_active: boolean
  school_count?: number
  event_count?: number
}

interface School {
  id: string
  name: string
  school_name?: string  // Some APIs use school_name
  district?: string
  district_id?: string
  school_type?: string
  address?: string
  phone?: string
  website?: string
  bell_schedule?: {
    start_time?: string
    end_time?: string
  }
}

interface CalendarEvent {
  id: string
  school_id?: string
  event_date: string
  event_type: string
  event_name: string
  name?: string  // Some events use 'name' instead of 'event_name'
  description?: string
  location?: string  // Location/venue for the event (especially for sports)
  game_type?: 'home' | 'away'  // For sports events: home or away game
  url?: string  // URL for event details or links
  start_time?: string
  end_time?: string
  affects_transportation?: boolean
  no_school?: boolean
  early_release?: boolean
  remote_learning?: boolean
  is_recurring?: boolean
  recurrence_pattern?: string
  source?: 'school' | 'district'
  school_name?: string
  district_name?: string
  all_day?: boolean
}

// Helper function to format time from 24-hour to 12-hour format
const formatTime = (timeStr: string | undefined): string => {
  if (!timeStr) return ''
  try {
    const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})/)
    if (timeMatch) {
      const hours = parseInt(timeMatch[1])
      const minutes = timeMatch[2]
      const ampm = hours >= 12 ? 'PM' : 'AM'
      const displayHours = hours % 12 || 12
      return `${displayHours}:${minutes} ${ampm}`
    }
    return timeStr
  } catch {
    return timeStr
  }
}

export const SchoolCalendarManager: React.FC = () => {
  const [districts, setDistricts] = useState<District[]>([])
  const [schools, setSchools] = useState<School[]>([])
  const [selectedSchool, setSelectedSchool] = useState<string>('')
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddDistrict, setShowAddDistrict] = useState(false)
  const [showAddEvent, setShowAddEvent] = useState(false)
  const [eventScope, setEventScope] = useState<'school' | 'district'>('school')
  const [editingDistrict, setEditingDistrict] = useState<District | null>(null)
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null)
  const [viewMode, setViewMode] = useState<'districts' | 'school-events' | 'district-events'>('districts')
  const [selectedDistrictId, setSelectedDistrictId] = useState<string>('')
  const [calendarView, setCalendarView] = useState<'agenda' | 'day' | 'week' | 'month'>('agenda')
  const [currentDate, setCurrentDate] = useState(new Date())
  const [showAddSchool, setShowAddSchool] = useState(false)
  const [editingSchool, setEditingSchool] = useState<School | null>(null)
  
  const [newDistrict, setNewDistrict] = useState<Partial<District>>({
    district_name: '',
    district_code: '',
    school_year: '2024-2025',
    start_date: '2024-08-01',
    end_date: '2025-05-31',
    color: '#3B82F6',
    is_active: true
  })
  
  const [newSchool, setNewSchool] = useState<Partial<School>>({
    school_name: '',
    district_id: '',
    school_type: 'Elementary',
    address: '',
    phone: '',
    website: '',
    bell_schedule: { start_time: '08:00', end_time: '15:00' }
  })
  
  const [newEvent, setNewEvent] = useState<Partial<CalendarEvent>>({
    event_date: format(new Date(), 'yyyy-MM-dd'),
    event_type: 'holiday',
    event_name: '',
    description: '',
    location: '',
    game_type: undefined,
    url: '',
    affects_transportation: false,
    all_day: true
  })

  useEffect(() => {
    loadData()
  }, [])

  useEffect(() => {
    if (selectedSchool) {
      loadSchoolCalendar(selectedSchool)
    }
  }, [selectedSchool])

  const loadData = async () => {
    setLoading(true)
    try {
      const [districtsData, schoolsData] = await Promise.all([
        apiClient.get('/api/admin/district-calendars'),
        apiClient.get('/api/schools')
      ])
      setDistricts(districtsData)
      setSchools(schoolsData)

      // Auto-select first school if available
      if (schoolsData.length > 0 && !selectedSchool) {
        setSelectedSchool(schoolsData[0].id)
      }

      // Auto-select first district if available
      if (districtsData.length > 0 && !selectedDistrictId) {
        setSelectedDistrictId(districtsData[0].id)
      }
    } catch (error) {
      console.error('Failed to load data:', error)
      toast.error('Failed to load schools and districts')
    } finally {
      setLoading(false)
    }
  }

  const loadSchoolCalendar = async (schoolId: string) => {
    try {
      const data = await apiClient.get(`/api/admin/schools/${schoolId}/calendar`)
      // Sort events chronologically starting from today
      const today = startOfToday()
      const sortedEvents = data.sort((a: CalendarEvent, b: CalendarEvent) => {
        const dateA = parseISO(a.event_date)
        const dateB = parseISO(b.event_date)
        // Put future events first, then sort by date
        const aIsFuture = isAfter(dateA, today) || isSameDay(dateA, today)
        const bIsFuture = isAfter(dateB, today) || isSameDay(dateB, today)
        
        if (aIsFuture && !bIsFuture) return -1
        if (!aIsFuture && bIsFuture) return 1
        
        return dateA.getTime() - dateB.getTime()
      })
      setEvents(sortedEvents)
    } catch (error) {
      console.error('Failed to load calendar:', error)
      toast.error('Failed to load school calendar')
    }
  }

  const loadDistrictEvents = async (districtId?: string) => {
    try {
      const url = districtId 
        ? `/api/admin/school-events?district_id=${districtId}`
        : '/api/admin/school-events'
      const data = await apiClient.get(url)
      // Sort events chronologically starting from today
      const today = startOfToday()
      const sortedEvents = data.sort((a: CalendarEvent, b: CalendarEvent) => {
        const dateA = parseISO(a.event_date)
        const dateB = parseISO(b.event_date)
        // Put future events first, then sort by date
        const aIsFuture = isAfter(dateA, today) || isSameDay(dateA, today)
        const bIsFuture = isAfter(dateB, today) || isSameDay(dateB, today)
        
        if (aIsFuture && !bIsFuture) return -1
        if (!aIsFuture && bIsFuture) return 1
        
        return dateA.getTime() - dateB.getTime()
      })
      setEvents(sortedEvents)
    } catch (error) {
      console.error('Failed to load district events:', error)
      toast.error('Failed to load district events')
    }
  }

  const handleCreateDistrict = async () => {
    try {
      await apiClient.post('/api/admin/district-calendars', newDistrict)
      toast.success('District created successfully')
      setShowAddDistrict(false)
      setNewDistrict({
        district_name: '',
        district_code: '',
        school_year: '2024-2025',
        start_date: '2024-08-01',
        end_date: '2025-05-31',
        color: '#3B82F6',
        is_active: true
      })
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create district')
    }
  }

  const handleUpdateDistrict = async () => {
    if (!editingDistrict) return
    
    try {
      await apiClient.patch(`/api/admin/district-calendars/${editingDistrict.id}`, editingDistrict)
      toast.success('District updated successfully')
      setEditingDistrict(null)
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update district')
    }
  }

  const handleDeleteDistrict = async (districtId: string) => {
    if (!confirm('Are you sure? This will delete all associated data.')) return
    
    try {
      await apiClient.delete(`/api/admin/district-calendars/${districtId}`)
      toast.success('District deleted successfully')
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete district')
    }
  }

  const handleCreateEvent = async () => {
    if (viewMode === 'school-events' && !selectedSchool) {
      toast.error('Please select a school first')
      return
    }
    
    try {
      // Ensure we send both name and event_name for compatibility
      const createData = {
        ...newEvent,
        name: newEvent.event_name,
        event_name: newEvent.event_name,
        // Ensure date is in correct format (handles both date-only and datetime strings)
        event_date: extractDateInTimezoneSync(newEvent.event_date)
      }
      
      if (viewMode === 'district-events') {
        // Create district event
        await apiClient.post('/api/admin/school-events', {
          ...createData,
          district_id: districts.find(d => d.is_active)?.id
        })
        loadDistrictEvents(selectedDistrictId || undefined)
      } else {
        // Create school event
        await apiClient.post(`/api/admin/schools/${selectedSchool}/calendar`, createData)
        loadSchoolCalendar(selectedSchool)
      }
      
      toast.success('Event created successfully')
      setShowAddEvent(false)
      setNewEvent({
        event_date: format(new Date(), 'yyyy-MM-dd'),
        event_type: 'holiday',
        event_name: '',
        description: '',
        location: '',
        game_type: undefined,
        url: '',
        affects_transportation: false,
        all_day: true
      })
    } catch (error: any) {
      console.error('Failed to create event:', error)
      toast.error(error.response?.data?.detail || 'Failed to create event')
    }
  }

  const handleUpdateEvent = async () => {
    if (!editingEvent) return
    
    try {
      // Ensure we send both name and event_name for compatibility
      const updateData = {
        ...editingEvent,
        name: editingEvent.event_name || editingEvent.name,
        event_name: editingEvent.event_name || editingEvent.name,
        // Ensure date is in correct format (handles both date-only and datetime strings)
        event_date: extractDateInTimezoneSync(editingEvent.event_date)
      }
      
      console.log('Updating event with data:', updateData)
      
      await apiClient.patch(`/api/admin/school-events/${editingEvent.id}`, updateData)
      toast.success('Event updated successfully')
      setEditingEvent(null)

      // Reload the appropriate view
      if (viewMode === 'district-events') {
        loadDistrictEvents(selectedDistrictId || undefined)
      } else if (viewMode === 'school-events' && selectedSchool) {
        loadSchoolCalendar(selectedSchool)
      }
    } catch (error: any) {
      console.error('Failed to update event:', error)
      toast.error(error.response?.data?.detail || 'Failed to update event')
    }
  }

  const handleDeleteEvent = async (eventId: string) => {
    if (!confirm('Are you sure you want to delete this event?')) return
    
    try {
      await apiClient.delete(`/api/admin/school-events/${eventId}`)
      toast.success('Event deleted successfully')

      // Reload the appropriate view
      if (viewMode === 'district-events') {
        loadDistrictEvents(selectedDistrictId || undefined)
      } else if (viewMode === 'school-events' && selectedSchool) {
        loadSchoolCalendar(selectedSchool)
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete event')
    }
  }

  const handleCreateSchool = async () => {
    try {
      // API expects school_name but we also send name for compatibility
      const schoolData = {
        ...newSchool,
        name: newSchool.school_name
      }
      await apiClient.post('/api/schools', schoolData)
      toast.success('School created successfully')
      setShowAddSchool(false)
      setNewSchool({
        school_name: '',
        district_id: '',
        school_type: 'Elementary',
        address: '',
        phone: '',
        website: '',
        bell_schedule: { start_time: '08:00', end_time: '15:00' }
      })
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create school')
    }
  }

  const handleUpdateSchool = async () => {
    if (!editingSchool) return
    
    try {
      // Send both name and school_name for compatibility
      const updateData = {
        ...editingSchool,
        school_name: editingSchool.name || editingSchool.school_name
      }
      await apiClient.patch(`/api/schools/${editingSchool.id}`, updateData)
      toast.success('School updated successfully')
      setEditingSchool(null)
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update school')
    }
  }

  const handleDeleteSchool = async (schoolId: string) => {
    if (!confirm('Are you sure you want to delete this school?')) return
    
    try {
      await apiClient.delete(`/api/schools/${schoolId}`)
      toast.success('School deleted successfully')
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete school')
    }
  }

  const eventTypeColors = {
    holiday: 'bg-red-100 text-red-800',
    'early-release': 'bg-yellow-100 text-yellow-800',
    'no-school': 'bg-gray-100 text-gray-800',
    'in-service': 'bg-purple-100 text-purple-800',
    sports: 'bg-blue-100 text-blue-800',
    event: 'bg-green-100 text-green-800',
    other: 'bg-gray-100 text-gray-800'
  }

  // Calendar navigation functions
  const navigatePrevious = () => {
    if (calendarView === 'day') {
      setCurrentDate(prev => addDays(prev, -1))
    } else if (calendarView === 'week') {
      setCurrentDate(prev => subWeeks(prev, 1))
    } else if (calendarView === 'month') {
      setCurrentDate(prev => subMonths(prev, 1))
    }
  }

  const navigateNext = () => {
    if (calendarView === 'day') {
      setCurrentDate(prev => addDays(prev, 1))
    } else if (calendarView === 'week') {
      setCurrentDate(prev => addWeeks(prev, 1))
    } else if (calendarView === 'month') {
      setCurrentDate(prev => addMonths(prev, 1))
    }
  }

  const navigateToday = () => {
    setCurrentDate(new Date())
  }

  // Get calendar days for the view
  const getCalendarDays = () => {
    if (calendarView === 'week') {
      const start = startOfWeek(currentDate)
      const end = endOfWeek(currentDate)
      return eachDayOfInterval({ start, end })
    } else if (calendarView === 'month') {
      const start = startOfMonth(currentDate)
      const end = endOfMonth(currentDate)
      const startWeek = startOfWeek(start)
      const endWeek = endOfWeek(end)
      return eachDayOfInterval({ start: startWeek, end: endWeek })
    }
    return [currentDate]
  }

  // Get events for a specific day
  const getEventsForDay = (day: Date) => {
    return events.filter(event => isSameDay(parseISO(event.event_date), day))
  }

  // Render calendar grid
  const renderCalendarGrid = () => {
    const days = getCalendarDays()
    const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    
    if (calendarView === 'day') {
      const dayEvents = getEventsForDay(currentDate)
      return (
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-center mb-4">
            <h3 className="text-lg font-semibold">{format(currentDate, 'EEEE, MMMM d, yyyy')}</h3>
          </div>
          <div className="space-y-2">
            {dayEvents.length > 0 ? (
              dayEvents.map(event => renderEventCard(event))
            ) : (
              <div className="text-center py-8 text-gray-500">No events scheduled for this day</div>
            )}
          </div>
        </div>
      )
    }
    
    if (calendarView === 'week') {
      return (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="grid grid-cols-7 bg-gray-50">
            {weekDays.map((day, idx) => (
              <div key={day} className="py-2 px-2 text-center text-sm font-medium text-gray-700 border-r last:border-r-0">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 divide-x">
            {days.map((day, idx) => {
              const dayEvents = getEventsForDay(day)
              return (
                <div key={idx} className={`min-h-[120px] p-2 ${
                  isToday(day) ? 'bg-blue-50' : ''
                } ${!isSameMonth(day, currentDate) ? 'bg-gray-50' : ''}`}>
                  <div className={`text-sm font-medium mb-1 ${
                    isToday(day) ? 'text-blue-600' : 'text-gray-900'
                  }`}>
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map(event => (
                      <div 
                        key={event.id}
                        className={`text-xs p-1 rounded truncate ${
                          eventTypeColors[event.event_type as keyof typeof eventTypeColors] || eventTypeColors.other
                        }`}
                        title={event.event_name || event.name}
                      >
                        {event.event_name || event.name}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-xs text-gray-500">+{dayEvents.length - 3} more</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )
    }
    
    if (calendarView === 'month') {
      return (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="grid grid-cols-7 bg-gray-50">
            {weekDays.map((day, idx) => (
              <div key={day} className="py-2 px-2 text-center text-sm font-medium text-gray-700 border-r last:border-r-0">
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 divide-x divide-y">
            {days.map((day, idx) => {
              const dayEvents = getEventsForDay(day)
              return (
                <div key={idx} className={`min-h-[100px] p-2 ${
                  isToday(day) ? 'bg-blue-50' : ''
                } ${!isSameMonth(day, currentDate) ? 'bg-gray-50' : ''}`}>
                  <div className={`text-sm font-medium mb-1 ${
                    isToday(day) ? 'text-blue-600' : 
                    !isSameMonth(day, currentDate) ? 'text-gray-400' : 'text-gray-900'
                  }`}>
                    {format(day, 'd')}
                  </div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 2).map(event => (
                      <div 
                        key={event.id}
                        className={`text-xs p-1 rounded truncate ${
                          eventTypeColors[event.event_type as keyof typeof eventTypeColors] || eventTypeColors.other
                        }`}
                        title={event.event_name || event.name}
                      >
                        {event.event_name || event.name}
                      </div>
                    ))}
                    {dayEvents.length > 2 && (
                      <div className="text-xs text-gray-500">+{dayEvents.length - 2} more</div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )
    }
    
    return null
  }

  // Render individual event card for agenda/day view
  const renderEventCard = (event: CalendarEvent) => (
    <div 
      key={event.id} 
      className={`bg-white rounded-lg shadow p-4 ${
        event.source === 'district' ? 'border-l-4 border-purple-500' : ''
      }`}
    >
      <div className="flex justify-between items-start gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <Calendar className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <span className="font-medium whitespace-nowrap">
              {format(parseISO(event.event_date), 'MMM d, yyyy')}
            </span>
            <span className={`px-2 py-1 rounded text-xs whitespace-nowrap ${
              eventTypeColors[event.event_type as keyof typeof eventTypeColors] || eventTypeColors.other
            }`}>
              {event.event_type}
            </span>
            {event.district_name && (
              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs whitespace-nowrap">
                {event.district_name}
              </span>
            )}
            {event.school_name && viewMode === 'district-events' && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs whitespace-nowrap">
                {event.school_name}
              </span>
            )}
          </div>
          <h4 className="text-lg font-medium mt-2">{event.event_name || event.name}</h4>
          {event.description && (
            <p className="text-gray-600 mt-1 break-words overflow-hidden max-w-full">
              {event.description.length > 200 
                ? `${event.description.substring(0, 200)}...` 
                : event.description}
            </p>
          )}
          {event.url && (
            <a 
              href={event.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 mt-1 text-sm"
            >
              <LinkIcon className="w-3 h-3 mr-1" />
              View Details
            </a>
          )}
          <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
            {event.start_time && (
              <span className="flex items-center">
                <Clock className="w-3 h-3 mr-1" />
                {formatTime(event.start_time)} - {formatTime(event.end_time)}
              </span>
            )}
            {event.affects_transportation && (
              <span className="flex items-center text-orange-600">
                <Bus className="w-3 h-3 mr-1" />
                Affects Transportation
              </span>
            )}
            {event.no_school && (
              <span className="flex items-center text-red-600 font-medium">
                <XCircle className="w-3 h-3 mr-1" />
                No School
              </span>
            )}
            {event.early_release && (
              <span className="flex items-center text-blue-600 font-medium">
                <Clock3 className="w-3 h-3 mr-1" />
                Early Release
              </span>
            )}
            {event.remote_learning && (
              <span className="flex items-center text-purple-600 font-medium">
                <Laptop className="w-3 h-3 mr-1" />
                Remote Learning
              </span>
            )}
            {event.is_recurring && (
              <span className="flex items-center">
                <RefreshCw className="w-3 h-3 mr-1" />
                Recurring
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          <button
            onClick={() => {
              console.log('Editing event:', event)
              // Infer all_day value if not explicitly set
              const inferredAllDay = event.all_day ?? (!event.start_time && !event.end_time)
              setEditingEvent({...event, all_day: inferredAllDay})
              setShowAddEvent(false)
            }}
            className="p-1 text-blue-600 hover:text-blue-900"
            title="Edit Event"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDeleteEvent(event.id)}
            className="p-1 text-red-600 hover:text-red-900"
            title="Delete Event"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">School Calendar Management</h2>
        <p className="text-gray-600">Manage districts, schools, and calendar events</p>
      </div>

      {/* View Mode Toggle - Three Primary Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6">
        <button
          onClick={() => setViewMode('districts')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            viewMode === 'districts'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Districts & Schools
        </button>
        <button
          onClick={() => {
            setViewMode('school-events')
            if (selectedSchool) loadSchoolCalendar(selectedSchool)
          }}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            viewMode === 'school-events'
              ? 'bg-white text-blue-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          School Events
        </button>
        <button
          onClick={() => {
            setViewMode('district-events')
            // Load events respecting the current district filter
            loadDistrictEvents(selectedDistrictId || undefined)
          }}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
            viewMode === 'district-events'
              ? 'bg-white text-purple-600 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          District Events
        </button>
      </div>

      {/* Districts View */}
      {viewMode === 'districts' && (
        <div>
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setShowAddDistrict(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add District
            </button>
            <button
              onClick={() => {
                setEditingSchool(null)
                setNewSchool({
                  school_name: '',
                  district_id: districts[0]?.id || '',
                  school_type: 'Elementary',
                  address: '',
                  phone: '',
                  website: '',
                  bell_schedule: { start_time: '08:00', end_time: '15:00' }
                })
                setShowAddSchool(true)
              }}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center"
            >
              <School className="w-4 h-4 mr-2" />
              Add School
            </button>
          </div>

          <div className="space-y-4">
            {districts.map(district => (
              <div key={district.id} className="bg-white rounded-lg shadow">
                {/* District Header */}
                <div className="p-6 border-b">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {district.district_name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Code: {district.district_code} | School Year: {district.school_year}
                      </p>
                      <p className="text-sm text-gray-500 mt-1">
                        {format(parseISO(district.start_date), 'MMM d, yyyy')} - {format(parseISO(district.end_date), 'MMM d, yyyy')}
                      </p>
                      <div className="flex items-center mt-2 space-x-4">
                        <span className="text-sm text-gray-600">
                          {district.school_count || 0} schools
                        </span>
                        <span className="text-sm text-gray-600">
                          {district.event_count || 0} events
                        </span>
                        <span className={`px-2 py-1 rounded text-xs ${
                          district.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {district.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setEditingDistrict(district)}
                        className="p-2 text-blue-600 hover:text-blue-900"
                        title="Edit District"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteDistrict(district.id)}
                        className="p-2 text-red-600 hover:text-red-900"
                        title="Delete District"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Schools List */}
                <div className="p-4 bg-gray-50">
                  <div className="space-y-2">
                    {schools
                      .filter(school => {
                        // Match schools to districts more flexibly
                        if (!school.district) return false
                        const schoolDistrict = school.district.toLowerCase()
                        const districtName = district.district_name.toLowerCase()
                        // Check if district name contains school district or vice versa
                        return districtName.includes(schoolDistrict) || 
                               schoolDistrict.includes(districtName) ||
                               school.district === district.district_name
                      })
                      .map(school => (
                        <div key={school.id} className="bg-white rounded-lg p-3 flex justify-between items-center">
                          <div className="flex items-center space-x-3">
                            <School className="w-4 h-4 text-gray-500" />
                            <div>
                              <h4 className="font-medium text-gray-900">{school.name}</h4>
                              {school.address && (
                                <p className="text-xs text-gray-500">{school.address}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                setSelectedSchool(school.id)
                                setViewMode('school-events')
                                loadSchoolCalendar(school.id)
                              }}
                              className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                            >
                              View Events
                            </button>
                            <button
                              onClick={() => {
                                setEditingSchool(school)
                                setShowAddSchool(true)
                              }}
                              className="p-1 text-gray-600 hover:text-gray-900"
                              title="Edit School"
                            >
                              <Edit2 className="w-3 h-3" />
                            </button>
                            <button
                              onClick={() => handleDeleteSchool(school.id)}
                              className="p-1 text-red-600 hover:text-red-900"
                              title="Delete School"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                      ))
                    }
                    {schools.filter(school => {
                      if (!school.district) return false
                      const schoolDistrict = school.district.toLowerCase()
                      const districtName = district.district_name.toLowerCase()
                      return districtName.includes(schoolDistrict) || 
                             schoolDistrict.includes(districtName) ||
                             school.district === district.district_name
                    }).length === 0 && (
                      <div className="text-center py-3 text-gray-500 text-sm">
                        No schools in this district yet
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* School Events View */}
      {viewMode === 'school-events' && (
        <div>
          <div className="mb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <select
                  value={selectedSchool}
                  onChange={(e) => setSelectedSchool(e.target.value)}
                  className="px-4 py-2 border rounded-lg"
                >
                  <option value="">Select a school...</option>
                  {schools.map(school => (
                    <option key={school.id} value={school.id}>
                      {school.name} {school.district && `(${school.district})`}
                    </option>
                  ))}
                </select>
                
                {/* Calendar View Selector */}
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setCalendarView('agenda')}
                    className={`px-3 py-1 rounded text-sm flex items-center ${
                      calendarView === 'agenda' ? 'bg-white shadow-sm' : ''
                    }`}
                    title="Agenda View"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCalendarView('day')}
                    className={`px-3 py-1 rounded text-sm flex items-center ${
                      calendarView === 'day' ? 'bg-white shadow-sm' : ''
                    }`}
                    title="Day View"
                  >
                    Day
                  </button>
                  <button
                    onClick={() => setCalendarView('week')}
                    className={`px-3 py-1 rounded text-sm flex items-center ${
                      calendarView === 'week' ? 'bg-white shadow-sm' : ''
                    }`}
                    title="Week View"
                  >
                    Week
                  </button>
                  <button
                    onClick={() => setCalendarView('month')}
                    className={`px-3 py-1 rounded text-sm flex items-center ${
                      calendarView === 'month' ? 'bg-white shadow-sm' : ''
                    }`}
                    title="Month View"
                  >
                    <CalendarDays className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {selectedSchool && (
                <button
                  onClick={() => {
                    setEventScope('school')
                    setShowAddEvent(true)
                  }}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add School Event
                </button>
              )}
            </div>
            
            {/* Calendar Navigation */}
            {calendarView !== 'agenda' && (
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={navigatePrevious}
                    className="p-2 hover:bg-gray-100 rounded"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={navigateToday}
                    className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Today
                  </button>
                  <button
                    onClick={navigateNext}
                    className="p-2 hover:bg-gray-100 rounded"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
                <div className="text-lg font-semibold">
                  {calendarView === 'day' && format(currentDate, 'MMMM d, yyyy')}
                  {calendarView === 'week' && `Week of ${format(startOfWeek(currentDate), 'MMM d, yyyy')}`}
                  {calendarView === 'month' && format(currentDate, 'MMMM yyyy')}
                </div>
              </div>
            )}
          </div>

          {/* School Events Display */}
          {selectedSchool && (
            <div>
              {calendarView === 'agenda' ? (
                <div className="space-y-2">
                  {events.map(event => renderEventCard(event))}
                </div>
              ) : (
                renderCalendarGrid()
              )}
              
              {calendarView === 'agenda' && events.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No events found for this school
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* District Events View */}
      {viewMode === 'district-events' && (
        <div>
          <div className="mb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <select
                  value={selectedDistrictId}
                  onChange={(e) => {
                    setSelectedDistrictId(e.target.value)
                    // Load events for selected district (or all if empty)
                    loadDistrictEvents(e.target.value || undefined)
                  }}
                  className="px-4 py-2 border rounded-lg"
                >
                  <option value="">All Districts</option>
                  {districts.map(district => (
                    <option key={district.id} value={district.id}>
                      {district.district_name}
                    </option>
                  ))}
                </select>
                
                {/* Calendar View Selector */}
                <div className="flex bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setCalendarView('agenda')}
                    className={`px-3 py-1 rounded text-sm flex items-center ${
                      calendarView === 'agenda' ? 'bg-white shadow-sm' : ''
                    }`}
                    title="Agenda View"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setCalendarView('day')}
                    className={`px-3 py-1 rounded text-sm flex items-center ${
                      calendarView === 'day' ? 'bg-white shadow-sm' : ''
                    }`}
                    title="Day View"
                  >
                    Day
                  </button>
                  <button
                    onClick={() => setCalendarView('week')}
                    className={`px-3 py-1 rounded text-sm flex items-center ${
                      calendarView === 'week' ? 'bg-white shadow-sm' : ''
                    }`}
                    title="Week View"
                  >
                    Week
                  </button>
                  <button
                    onClick={() => setCalendarView('month')}
                    className={`px-3 py-1 rounded text-sm flex items-center ${
                      calendarView === 'month' ? 'bg-white shadow-sm' : ''
                    }`}
                    title="Month View"
                  >
                    <CalendarDays className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <button
                onClick={() => {
                  setEventScope('district')
                  setShowAddEvent(true)
                }}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add District Event
              </button>
            </div>
            
            {/* Calendar Navigation */}
            {calendarView !== 'agenda' && (
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={navigatePrevious}
                    className="p-2 hover:bg-gray-100 rounded"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={navigateToday}
                    className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    Today
                  </button>
                  <button
                    onClick={navigateNext}
                    className="p-2 hover:bg-gray-100 rounded"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
                <div className="text-lg font-semibold">
                  {calendarView === 'day' && format(currentDate, 'MMMM d, yyyy')}
                  {calendarView === 'week' && `Week of ${format(startOfWeek(currentDate), 'MMM d, yyyy')}`}
                  {calendarView === 'month' && format(currentDate, 'MMMM yyyy')}
                </div>
              </div>
            )}
          </div>

          {/* District Events Display */}
          <div>
            {calendarView === 'agenda' ? (
              <div className="space-y-2">
                {events.map(event => renderEventCard(event))}
              </div>
            ) : (
              renderCalendarGrid()
            )}
            
            {calendarView === 'agenda' && events.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No district events found
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit District Modal */}
      {(showAddDistrict || editingDistrict) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingDistrict ? 'Edit District' : 'Add New District'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">District Name *</label>
                <input
                  type="text"
                  value={editingDistrict ? editingDistrict.district_name : newDistrict.district_name}
                  onChange={(e) => editingDistrict 
                    ? setEditingDistrict({ ...editingDistrict, district_name: e.target.value })
                    : setNewDistrict({ ...newDistrict, district_name: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">District Code</label>
                  <input
                    type="text"
                    value={editingDistrict ? editingDistrict.district_code : newDistrict.district_code}
                    onChange={(e) => editingDistrict
                      ? setEditingDistrict({ ...editingDistrict, district_code: e.target.value })
                      : setNewDistrict({ ...newDistrict, district_code: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">School Year</label>
                  <input
                    type="text"
                    value={editingDistrict ? editingDistrict.school_year : newDistrict.school_year}
                    onChange={(e) => editingDistrict
                      ? setEditingDistrict({ ...editingDistrict, school_year: e.target.value })
                      : setNewDistrict({ ...newDistrict, school_year: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Start Date</label>
                  <input
                    type="date"
                    value={editingDistrict ? editingDistrict.start_date : newDistrict.start_date}
                    onChange={(e) => editingDistrict
                      ? setEditingDistrict({ ...editingDistrict, start_date: e.target.value })
                      : setNewDistrict({ ...newDistrict, start_date: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End Date</label>
                  <input
                    type="date"
                    value={editingDistrict ? editingDistrict.end_date : newDistrict.end_date}
                    onChange={(e) => editingDistrict
                      ? setEditingDistrict({ ...editingDistrict, end_date: e.target.value })
                      : setNewDistrict({ ...newDistrict, end_date: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editingDistrict ? editingDistrict.is_active : newDistrict.is_active}
                  onChange={(e) => editingDistrict
                    ? setEditingDistrict({ ...editingDistrict, is_active: e.target.checked })
                    : setNewDistrict({ ...newDistrict, is_active: e.target.checked })
                  }
                  className="mr-2"
                />
                <label className="text-sm">Active</label>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddDistrict(false)
                  setEditingDistrict(null)
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={editingDistrict ? handleUpdateDistrict : handleCreateDistrict}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                {editingDistrict ? 'Save Changes' : 'Create District'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Event Modal */}
      {(showAddEvent || editingEvent) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              {editingEvent ? 'Edit Event' : 'Add New Event'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Event Name *</label>
                <input
                  type="text"
                  value={editingEvent ? (editingEvent.event_name || editingEvent.name || '') : (newEvent.event_name || '')}
                  onChange={(e) => editingEvent
                    ? setEditingEvent({ ...editingEvent, event_name: e.target.value, name: e.target.value })
                    : setNewEvent({ ...newEvent, event_name: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Date *</label>
                  <input
                    type="date"
                    value={editingEvent ? editingEvent.event_date : newEvent.event_date}
                    onChange={(e) => editingEvent
                      ? setEditingEvent({ ...editingEvent, event_date: e.target.value })
                      : setNewEvent({ ...newEvent, event_date: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Type</label>
                  <select
                    value={editingEvent ? editingEvent.event_type : newEvent.event_type}
                    onChange={(e) => {
                      const isInService = e.target.value === 'in-service';
                      if (editingEvent) {
                        setEditingEvent({
                          ...editingEvent,
                          event_type: e.target.value,
                          no_school: isInService ? true : editingEvent.no_school
                        });
                      } else {
                        setNewEvent({
                          ...newEvent,
                          event_type: e.target.value,
                          no_school: isInService ? true : newEvent.no_school
                        });
                      }
                    }}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="holiday">Holiday</option>
                    <option value="no-school">No School</option>
                    <option value="in-service">In-Service (No School)</option>
                    <option value="early-release">Early Release</option>
                    <option value="sports">Sports</option>
                    <option value="event">Event</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              {/* All-day checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="all-day-event"
                  checked={editingEvent
                    ? (editingEvent.all_day ?? (!editingEvent.start_time && !editingEvent.end_time))
                    : (newEvent.all_day ?? (!newEvent.start_time && !newEvent.end_time))
                  }
                  onChange={(e) => editingEvent
                    ? setEditingEvent({ ...editingEvent, all_day: e.target.checked })
                    : setNewEvent({ ...newEvent, all_day: e.target.checked })
                  }
                  className="mr-2"
                />
                <label htmlFor="all-day-event" className="text-sm">All-day event</label>
              </div>

              {/* Time fields - only show if not all-day */}
              {!(editingEvent ? (editingEvent.all_day !== false) : (newEvent.all_day !== false)) && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Start Time</label>
                    <input
                      type="time"
                      value={editingEvent ? (editingEvent.start_time || '') : (newEvent.start_time || '')}
                      onChange={(e) => editingEvent
                        ? setEditingEvent({ ...editingEvent, start_time: e.target.value })
                        : setNewEvent({ ...newEvent, start_time: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">End Time</label>
                    <input
                      type="time"
                      value={editingEvent ? (editingEvent.end_time || '') : (newEvent.end_time || '')}
                      onChange={(e) => editingEvent
                        ? setEditingEvent({ ...editingEvent, end_time: e.target.value })
                        : setNewEvent({ ...newEvent, end_time: e.target.value })
                      }
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  value={editingEvent ? (editingEvent.description || '') : (newEvent.description || '')}
                  onChange={(e) => editingEvent
                    ? setEditingEvent({ ...editingEvent, description: e.target.value })
                    : setNewEvent({ ...newEvent, description: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                />
              </div>

              {/* Sports-specific fields */}
              {((editingEvent?.event_type === 'sports') || (newEvent.event_type === 'sports')) && (
                <>
                  {/* Home/Away selector */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Game Location Type</label>
                    <div className="flex gap-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="game-type"
                          value="home"
                          checked={(editingEvent?.game_type || newEvent.game_type) === 'home'}
                          onChange={(e) => editingEvent
                            ? setEditingEvent({ ...editingEvent, game_type: 'home' })
                            : setNewEvent({ ...newEvent, game_type: 'home' })
                          }
                          className="mr-2"
                        />
                        <span className="text-sm">🏠 Home Game</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="game-type"
                          value="away"
                          checked={(editingEvent?.game_type || newEvent.game_type) === 'away'}
                          onChange={(e) => editingEvent
                            ? setEditingEvent({ ...editingEvent, game_type: 'away' })
                            : setNewEvent({ ...newEvent, game_type: 'away' })
                          }
                          className="mr-2"
                        />
                        <span className="text-sm">🚌 Away Game</span>
                      </label>
                    </div>
                  </div>

                  {/* Location field with address lookup */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Venue/Location
                      <span className="text-red-500"> *</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={editingEvent ? (editingEvent.location || '') : (newEvent.location || '')}
                        onChange={(e) => editingEvent
                          ? setEditingEvent({ ...editingEvent, location: e.target.value })
                          : setNewEvent({ ...newEvent, location: e.target.value })
                        }
                        className="flex-1 px-3 py-2 border rounded-lg"
                        placeholder="e.g., King's Academy or 123 School Rd, City, GA"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const searchQuery = editingEvent?.location || newEvent.location || '';
                          if (searchQuery) {
                            window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchQuery)}`, '_blank', 'width=1000,height=800');
                          } else {
                            window.open('https://www.google.com/maps', '_blank', 'width=1000,height=800');
                          }
                        }}
                        className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 whitespace-nowrap text-sm"
                      >
                        🔍 Search Maps
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Venue name or full address. Click "Search Maps" to find and verify the address.
                    </p>
                  </div>
                </>
              )}

              {/* Location field for non-sports events */}
              {((editingEvent?.event_type !== 'sports') && (newEvent.event_type !== 'sports')) &&
                ((editingEvent?.location) || (newEvent.location)) && (
                <div>
                  <label className="block text-sm font-medium mb-1">Location</label>
                  <input
                    type="text"
                    value={editingEvent ? (editingEvent.location || '') : (newEvent.location || '')}
                    onChange={(e) => editingEvent
                      ? setEditingEvent({ ...editingEvent, location: e.target.value })
                      : setNewEvent({ ...newEvent, location: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Event location or venue"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">Event URL (optional)</label>
                <input
                  type="url"
                  value={editingEvent ? (editingEvent.url || '') : (newEvent.url || '')}
                  onChange={(e) => editingEvent
                    ? setEditingEvent({ ...editingEvent, url: e.target.value })
                    : setNewEvent({ ...newEvent, url: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="https://example.com/event-details"
                />
                <p className="text-xs text-gray-500 mt-1">Add a link for more information about this event</p>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="no-school-day"
                  checked={editingEvent ? (editingEvent.no_school || false) : (newEvent.no_school || false)}
                  onChange={(e) => editingEvent
                    ? setEditingEvent({ ...editingEvent, no_school: e.target.checked, early_release: false, remote_learning: false })
                    : setNewEvent({ ...newEvent, no_school: e.target.checked, early_release: false, remote_learning: false })
                  }
                  className="mr-2"
                />
                <label htmlFor="no-school-day" className="text-sm">No School Day</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="early-release"
                  checked={editingEvent ? (editingEvent.early_release || false) : (newEvent.early_release || false)}
                  onChange={(e) => editingEvent
                    ? setEditingEvent({ ...editingEvent, early_release: e.target.checked, no_school: false, remote_learning: false })
                    : setNewEvent({ ...newEvent, early_release: e.target.checked, no_school: false, remote_learning: false })
                  }
                  className="mr-2"
                />
                <label htmlFor="early-release" className="text-sm">Early Release</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="remote-learning"
                  checked={editingEvent ? (editingEvent.remote_learning || false) : (newEvent.remote_learning || false)}
                  onChange={(e) => editingEvent
                    ? setEditingEvent({ ...editingEvent, remote_learning: e.target.checked, no_school: false, early_release: false })
                    : setNewEvent({ ...newEvent, remote_learning: e.target.checked, no_school: false, early_release: false })
                  }
                  className="mr-2"
                />
                <label htmlFor="remote-learning" className="text-sm">Remote Learning</label>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="affects-transportation"
                  checked={editingEvent ? (editingEvent.affects_transportation || false) : (newEvent.affects_transportation || false)}
                  onChange={(e) => editingEvent
                    ? setEditingEvent({ ...editingEvent, affects_transportation: e.target.checked })
                    : setNewEvent({ ...newEvent, affects_transportation: e.target.checked })
                  }
                  className="mr-2"
                />
                <label htmlFor="affects-transportation" className="text-sm">Affects Transportation</label>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddEvent(false)
                  setEditingEvent(null)
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={editingEvent ? handleUpdateEvent : handleCreateEvent}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                {editingEvent ? 'Save Changes' : 'Create Event'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit School Modal */}
      {showAddSchool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl my-8">
            <h3 className="text-lg font-semibold mb-4">
              {editingSchool ? 'Edit School' : 'Add New School'}
            </h3>
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">School Name *</label>
                  <input
                    type="text"
                    value={editingSchool ? (editingSchool.name || editingSchool.school_name || '') : (newSchool.school_name || '')}
                    onChange={(e) => editingSchool
                      ? setEditingSchool({ ...editingSchool, name: e.target.value, school_name: e.target.value })
                      : setNewSchool({ ...newSchool, school_name: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="e.g., Kell High School"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">District *</label>
                  <select
                    value={editingSchool ? editingSchool.district_id : newSchool.district_id}
                    onChange={(e) => editingSchool
                      ? setEditingSchool({ ...editingSchool, district_id: e.target.value })
                      : setNewSchool({ ...newSchool, district_id: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="">Select District</option>
                    {districts.map(district => (
                      <option key={district.id} value={district.id}>
                        {district.district_name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">School Type</label>
                  <select
                    value={editingSchool ? editingSchool.school_type : newSchool.school_type}
                    onChange={(e) => editingSchool
                      ? setEditingSchool({ ...editingSchool, school_type: e.target.value })
                      : setNewSchool({ ...newSchool, school_type: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="Elementary">Elementary</option>
                    <option value="Middle">Middle</option>
                    <option value="High">High School</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Phone</label>
                  <input
                    type="tel"
                    value={editingSchool ? editingSchool.phone : newSchool.phone || ''}
                    onChange={(e) => editingSchool
                      ? setEditingSchool({ ...editingSchool, phone: e.target.value })
                      : setNewSchool({ ...newSchool, phone: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Address</label>
                <input
                  type="text"
                  value={editingSchool ? editingSchool.address : newSchool.address || ''}
                  onChange={(e) => editingSchool
                    ? setEditingSchool({ ...editingSchool, address: e.target.value })
                    : setNewSchool({ ...newSchool, address: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="123 School St, City, State 12345"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Website</label>
                <input
                  type="url"
                  value={editingSchool ? editingSchool.website : newSchool.website || ''}
                  onChange={(e) => editingSchool
                    ? setEditingSchool({ ...editingSchool, website: e.target.value })
                    : setNewSchool({ ...newSchool, website: e.target.value })
                  }
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="https://school.edu"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Bell Schedule</label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Start Time</label>
                    <input
                      type="time"
                      value={editingSchool ? editingSchool.bell_schedule?.start_time : newSchool.bell_schedule?.start_time || '08:00'}
                      onChange={(e) => {
                        const schedule = editingSchool 
                          ? { ...editingSchool.bell_schedule, start_time: e.target.value }
                          : { ...newSchool.bell_schedule, start_time: e.target.value }
                        editingSchool
                          ? setEditingSchool({ ...editingSchool, bell_schedule: schedule })
                          : setNewSchool({ ...newSchool, bell_schedule: schedule })
                      }}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">End Time</label>
                    <input
                      type="time"
                      value={editingSchool ? editingSchool.bell_schedule?.end_time : newSchool.bell_schedule?.end_time || '15:00'}
                      onChange={(e) => {
                        const schedule = editingSchool 
                          ? { ...editingSchool.bell_schedule, end_time: e.target.value }
                          : { ...newSchool.bell_schedule, end_time: e.target.value }
                        editingSchool
                          ? setEditingSchool({ ...editingSchool, bell_schedule: schedule })
                          : setNewSchool({ ...newSchool, bell_schedule: schedule })
                      }}
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowAddSchool(false)
                    setEditingSchool(null)
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Cancel
                </button>
                <button
                  onClick={editingSchool ? handleUpdateSchool : handleCreateSchool}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  {editingSchool ? 'Save Changes' : 'Create School'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unified Add Event Modal */}
      <UnifiedAddEventModal
        isOpen={showAddEvent && !editingEvent}
        onClose={() => setShowAddEvent(false)}
        onSuccess={() => {
          // Reload the appropriate view
          if (viewMode === 'district-events') {
            loadDistrictEvents(selectedDistrictId || undefined)
          } else if (viewMode === 'school-events' && selectedSchool) {
            loadSchoolCalendar(selectedSchool)
          }
          setShowAddEvent(false)
        }}
        initialScope={eventScope}
        isAdmin={true}
      />
    </div>
  )
}